import type { KeyboardEventHandler, MouseEventHandler, SVGProps } from "react"
import { memo } from "react"

interface LandingLogoLiveTheGameProps extends SVGProps<SVGSVGElement> {
  showVideo?: boolean | undefined
  /**
   * Optional video sources (masked GIF/MP4 pair) to render for the logo.
   * If none are provided, a generic fallback animation is used.
   */
  videoSrc?:
    | {
        mp4: string
        webm: string
      }
    | undefined
}

function LandingLogoLiveTheGameBase({
  showVideo = true,
  videoSrc,
  onClick,
  ...props
}: LandingLogoLiveTheGameProps & { onClick?: MouseEventHandler<HTMLDivElement> }) {
  // Use a unique gradient id each render to avoid collisions
  const gradientId = "paint0_linear_2642_3587"

  // Fallback animation used when a specific sport animation isn't supplied.
  const fallbackVideoSrc = {
    mp4: "https://media.giphy.com/media/6sydopdFoANKWuc47x/giphy.mp4",
    webm: "https://media.giphy.com/media/6sydopdFoANKWuc47x/giphy.webm",
  }

  const { mp4, webm } = videoSrc ?? fallbackVideoSrc

  // Source for the subtle always-playing overlay video
  const overlayVideoSrc = "https://a.bracky.app/assets/landing/overlays/logo-live-the%20game-blue-screen-shift.mp4"

  // Latest "LIVE THE GAME" word-mark path (615 × 247)
  const pathData =
    "M102.13 113.11C135.6 113.11 156.46 132.2 160.3 158.2H125.98C123.54 146.64 115.06 142.36 102.13 142.36C83.44 142.36 70.3399 156.99 70.3398 179.92C70.3398 202.85 83.7899 218.2 103.01 218.2C117.86 218.2 129.04 209.53 130.96 197.43H95.4805L103.01 170.71H166.18L144.97 231.93C134 241.2 119.49 246.73 102.66 246.73C64.0502 246.73 37.1504 219.65 37.1504 179.92C37.1504 140.19 63.87 113.11 102.13 113.11ZM279.02 239.65V114.74H325.7L342.55 166.07C345.09 174.41 349.13 188.8 352.38 201.17C355.63 188.8 359.58 174.41 362.03 166.07L378.7 114.74H425.47V245.48H394.5V188.88C394.5 179.93 395.2 163.35 395.55 147.82C391.16 163.7 386.69 180.11 383.88 188.88L365.1 245.48H339.57L320.44 188.88C317.72 180.46 313.33 164.84 309.03 149.57C309.38 164.57 310 180.28 310 188.88V245.48H245.77L238.05 220.3H191.7L183.64 245.48H149.33L161.97 208.69L164.7 200.74L178.78 159.76L185.03 141.58H173.27L180.44 114.73H235.85L279.02 239.65ZM534.74 114.74L527.57 141.59H468.6L468.46 141.6H461.98L466.83 147.33L461.31 166.77H525.06L517.66 192.83H468.47L461.32 218.63H534.74L527.51 245.48H435.32V114.74H534.74ZM215.14 143.08C213.3 149.67 206.95 170.64 201.58 188.34L193.71 195.64H230.34C225.14 178 218.3 154.62 215.14 143.08ZM541.61 114.68C543.38 114.68 544.86 115.29 546.04 116.51C547.22 117.73 547.81 119.24 547.81 121.04C547.81 122.84 547.22 124.35 546.04 125.57C544.86 126.79 543.38 127.4 541.61 127.4C539.84 127.4 538.36 126.79 537.18 125.57C536 124.35 535.41 122.84 535.41 121.04C535.41 119.24 536 117.73 537.18 116.51C538.36 115.29 539.84 114.68 541.61 114.68ZM541.61 116.07C540.22 116.07 539.09 116.54 538.2 117.49C537.32 118.44 536.87 119.62 536.87 121.04C536.87 122.46 537.31 123.64 538.2 124.59C539.08 125.54 540.22 126.01 541.61 126.01C543 126.01 544.131 125.54 545.021 124.59C545.9 123.64 546.35 122.46 546.35 121.04C546.35 119.62 545.91 118.44 545.021 117.49C544.141 116.54 543 116.07 541.61 116.07ZM541.81 117.66C542.48 117.66 543.01 117.84 543.39 118.19C543.77 118.54 543.96 119.01 543.96 119.61C543.96 120.1 543.82 120.51 543.55 120.85C543.28 121.18 542.91 121.4 542.45 121.49L544.49 124.44H543.14L541.13 121.56H540.58V124.44H539.39V117.66H541.81ZM540.58 120.43H541.75C542.41 120.43 542.73 120.16 542.73 119.61C542.73 119.06 542.4 118.79 541.75 118.79H540.58V120.43ZM36.04 75.8799V75.5801L34.7002 80.4902H78.2305L72.1299 102.64L72.1396 102.65H0L15.6904 45.71L28.4902 0H56.75L36.04 75.8799ZM135.14 0L107.08 102.65H78.3896L106.88 0H135.14ZM167.14 0L173.01 76.2197L213.12 0H242.01L188.06 102.65H145.69L137.7 0H167.14ZM325.3 0L318.93 22.1602H267.95L262.86 40.8203H306.13L306.08 40.9902L300.37 61.8203H257.11L251.96 80.5H301.91L295.81 102.65H217.25L232.94 45.71L245.74 0H325.3ZM432.93 0L426.5 22.1699H399.5L377.54 102.65H348.79L371.13 22.1699H343.29L349.72 0H432.93ZM468.14 0L457.49 39H485.82L496.64 0H524.9L496.84 102.65H468.15L479.96 60.0898H451.72L440.08 102.65H411.39L439.88 0H468.14ZM614.55 0L608.18 22.1602H557.19L552.1 40.8203H595.37L595.32 40.9902L589.61 61.8203H546.35L541.2 80.5H591.15L585.05 102.65H506.5L522.19 45.71L534.99 0H614.55Z"

  // Separate out the onClick so we don't pass it to the <svg> as well.
  const { onClick: _svgOnClick, ...svgProps } = props as SVGProps<SVGSVGElement>

  // Provide basic keyboard accessibility (Enter / Space triggers click)
  const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (!onClick) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      if (typeof onClick === "function") {
        // Cast to expected handler type to satisfy typings
        onClick(e as unknown as React.MouseEvent<SVGSVGElement>)
      }
    }
  }

  return (
    <button
      type="button"
      className="relative inline-block"
      onClick={onClick as unknown as MouseEventHandler<HTMLButtonElement>}
      onKeyDown={handleKeyDown as unknown as KeyboardEventHandler<HTMLButtonElement>}
      // Reset default button styles
      style={{ background: "transparent", padding: 0, border: "none" }}
    >
      {/* Sports videos masked by the SVG */}
      {showVideo && (
        <>
          <video
            key={mp4}
            autoPlay
            loop
            muted
            playsInline
            crossOrigin="anonymous"
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              maskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              WebkitMaskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              mixBlendMode: "overlay",
              opacity: 1,
              filter: "saturate(0) contrast(5)",
            }}
          >
            <source src={mp4} type="video/mp4" />
            <source src={webm} type="video/webm" />
          </video>

          {/* Scanlines overlay, clipped to the word-mark shape */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none scanlines"
            style={{
              opacity: 0.24,
              mixBlendMode: "color-burn",
              maskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              WebkitMaskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
            }}
          />

          {/* Texture overlay video – all devices */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{
              zIndex: 10,
              mixBlendMode: "soft-light",
              maskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              WebkitMaskImage: `url('data:image/svg+xml;utf8,<svg width="615" height="247" viewBox="0 0 615 247" xmlns="http://www.w3.org/2000/svg"><path d="${pathData}" fill="white"/></svg>')`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              opacity: 0.75,
            }}
          >
            <source src={overlayVideoSrc} type="video/mp4" />
          </video>
        </>
      )}

      {/* Original SVG as fallback */}
      <svg
        width="615"
        height="247"
        viewBox="0 0 615 247"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="LIVE THE GAME wordmark"
        className={showVideo ? "opacity-0" : ""}
        {...svgProps}
      >
        <title>LIVE THE GAME wordmark</title>
        <path d={pathData} fill={`url(#${gradientId})`} />
        <defs>
          <linearGradient id={gradientId} x1="0" y1="123.365" x2="614.55" y2="123.365" gradientUnits="userSpaceOnUse">
            <stop stopColor="#ffffff" />
            <stop offset="0.5" stopColor="#30DFFF" />
            <stop offset="0.75" stopColor="#0074FF" />
            <stop offset="1" stopColor="#001CFF" />
          </linearGradient>
        </defs>
      </svg>
    </button>
  )
}

export const LandingLogoLiveTheGame = memo(LandingLogoLiveTheGameBase)
